import { format, type Locale } from "date-fns";
import { bs, enUS, hr, sr } from "date-fns/locale";
import { ChevronRightIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import DeleteSessionDialog from "features/Session/DeleteSessionDialog";

import type { Session } from "types/Session.type";

import { ROUTES } from "utils/constants";

const dateFormats: Record<string, string> = {
  en: "MM/dd/yyyy HH:mm",
  hr: "dd.MM.yyyy. HH:mm",
  bs: "dd.MM.yyyy. HH:mm",
  sr: "dd.MM.yyyy. HH:mm",
};

const dateLocales: Record<string, Locale> = {
  en: enUS,
  hr: hr,
  bs: bs,
  sr: sr,
};

export default function SessionCard({ session }: { session: Session }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const lang = i18n.language.split("-")[0];
  const dateFormat = dateFormats[lang] || "dd.MM.yyyy. HH:mm";
  const dateLocale = dateLocales[lang] || hr;

  const navigateToSession = () =>
    navigate(ROUTES.SESSION_VIEW.replace(":id", session.id?.toString() ?? ""));

  return (
    <div
      className="p-4 border border-general-gray-400 rounded flex justify-between items-center cursor-pointer"
      onClick={navigateToSession}
    >
      <div className="flex flex-col gap-1.5 min-w-0 flex-1">
        {(session.sessionPatient?.firstName || session.sessionPatient?.lastName) && (
          <div className="font-semibold">
            {session.sessionPatient?.firstName} {session.sessionPatient?.lastName}
          </div>
        )}
        {session.sessionReport?.shortTranscription && (
          <div className="truncate">{session.sessionReport?.shortTranscription}</div>
        )}
        <div className="flex gap-6 text-sm text-general-gray-700">
          <div className="flex gap-2 flex-col md:flex-row">
            {session.updatedAt && (
              <span>
                {(() => {
                  const diffMinutes = Math.floor((Date.now() - new Date(session.updatedAt).getTime()) / 60000);
                  if (diffMinutes < 60) return t("timeAgo.minutes", { count: diffMinutes });
                  if (diffMinutes < 1440) return t("timeAgo.hours", { count: Math.floor(diffMinutes / 60) });
                  return t("timeAgo.days", { count: Math.floor(diffMinutes / 1440) });
                })()}
              </span>
            )}
            <span>
              {session.updatedAt ? format(new Date(session.updatedAt), dateFormat, { locale: dateLocale }) : "N/A"}
            </span>
          </div>
        </div>
      </div>
      <div className="flex gap-4 items-center" onClick={(e) => e.stopPropagation()}>
        <DeleteSessionDialog sessionId={session.id?.toString() ?? ""} />
        <ChevronRightIcon className="size-6 min-w-fit" onClick={navigateToSession} />
      </div>
    </div>
  );
}
